import { useState } from 'react';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const BORDER = '1px solid #d0d0cc';
const TITLE_BG = '#ececea';
const HEAD_BG = '#f4f4f2';

/**
 * Bordered, titled table that collapses to a single title row by default.
 * Click the title bar to expand/collapse.
 */
export default function OutwriteCollapsibleTable({
  title,
  summary,
  columns,
  children,
  actions = null,
  defaultExpanded = false,
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <Box
      sx={{
        border: BORDER,
        borderRadius: 1,
        overflow: 'hidden',
        bgcolor: '#fcfcfb',
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        onClick={() => setExpanded((v) => !v)}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setExpanded((v) => !v);
          }
        }}
        sx={{
          px: 1.5,
          py: 1,
          minHeight: 44,
          bgcolor: TITLE_BG,
          cursor: 'pointer',
          userSelect: 'none',
          borderBottom: expanded ? BORDER : 'none',
          '&:hover': { bgcolor: '#e4e4e0' },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
          <ExpandMoreIcon
            fontSize="small"
            sx={{
              color: 'text.secondary',
              transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
              transition: 'transform 160ms ease',
              flexShrink: 0,
            }}
          />
          <Typography
            component="span"
            sx={{
              fontWeight: 700,
              fontSize: 15,
              letterSpacing: 0.2,
              color: '#1a1a1a',
            }}
          >
            {title}
          </Typography>
          {!expanded && summary != null && summary !== '' && (
            <Typography
              component="span"
              variant="body2"
              color="text.secondary"
              noWrap
              sx={{ ml: 0.5 }}
            >
              · {summary}
            </Typography>
          )}
        </Stack>
        {actions && (
          <Box
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
            sx={{ flexShrink: 0 }}
          >
            {actions}
          </Box>
        )}
      </Stack>

      <Collapse in={expanded} timeout={200} unmountOnExit={false}>
        <TableContainer>
          <Table
            size="small"
            sx={{
              borderCollapse: 'collapse',
              '& th, & td': {
                border: BORDER,
              },
              '& th': {
                bgcolor: HEAD_BG,
                fontWeight: 700,
                fontSize: 12,
                letterSpacing: 0.3,
                textTransform: 'uppercase',
                color: '#333',
                py: 1,
              },
              '& td': {
                verticalAlign: 'top',
                py: 1,
              },
            }}
          >
            <TableHead>
              <TableRow>
                {columns.map((col) => (
                  <TableCell key={col.key} width={col.width} sx={col.sx}>
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>{children}</TableBody>
          </Table>
        </TableContainer>
      </Collapse>
    </Box>
  );
}
